import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import { Nav } from "@/components/Nav";
import { InstagramEmbed } from "@/components/InstagramEmbed";
import { fetchTrialPost } from "@/lib/api";
import { TRIAL } from "@/constants/testIds";

export default function InstagramTrialDetail() {
  const { shortcode } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["ig-trial-post", shortcode],
    queryFn: () => fetchTrialPost(shortcode),
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [shortcode]);

  if (isLoading) {
    return (
      <div
        className="min-h-screen bg-[#101010] text-[#F5F5F0] flex items-center justify-center font-mono text-xs uppercase tracking-widest text-[#555550]"
        data-testid="trial-detail-loading-state"
      >
        Loading…
      </div>
    );
  }

  if (isError || !data?.post) {
    return (
      <div className="min-h-screen bg-[#101010] text-[#F5F5F0]" data-testid="trial-detail-not-found">
        <Nav />
        <div className="flex flex-col items-center justify-center gap-4 py-32">
          <p className="font-mono text-xs uppercase tracking-widest text-[#888880]">Not part of this trial</p>
          <Link to="/instagram-trial" className="font-mono text-xs uppercase tracking-widest text-[#CCFF00] hover:underline">
            Back to Trial
          </Link>
        </div>
      </div>
    );
  }

  const { post, next } = data;

  return (
    <div className="min-h-screen bg-[#101010] text-[#F5F5F0]" data-testid={TRIAL.detailPage}>
      <Nav />
      <div className="max-w-3xl mx-auto px-3 sm:px-6 pt-4 sm:pt-6 flex items-center justify-between">
        <Link
          to="/instagram-trial"
          data-testid={TRIAL.backButton}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#888880] hover:text-[#CCFF00] transition-colors min-h-[44px]"
        >
          <ArrowLeft size={14} /> Back to Trial
        </Link>
        {next && (
          <Link
            to={`/instagram-trial/${next.shortcode}`}
            data-testid={TRIAL.nextButton}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#888880] hover:text-[#CCFF00] transition-colors min-h-[44px]"
          >
            Next: {next.brandName} <ArrowRight size={14} />
          </Link>
        )}
      </div>

      <div className="max-w-3xl mx-auto px-3 sm:px-6 mt-6">
        <InstagramEmbed postUrl={post.postUrl} shortcode={post.shortcode} />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
        <div className="flex items-center gap-3 flex-wrap mb-2">
          <h1 data-testid="trial-detail-brand" className="font-mono text-xl sm:text-2xl tracking-tight text-[#F5F5F0]">
            {post.brandName}
          </h1>
          <span className="font-mono text-[10px] uppercase tracking-widest bg-[#181818] text-[#CCFF00] px-2 py-1 border border-[#CCFF00]/40">
            Real EiPi Work
          </span>
        </div>
        <p className="font-mono text-xs sm:text-sm uppercase tracking-widest text-[#888880]">{post.brandCategory}</p>

        {post.captionWritten && (
          <p className="mt-6 font-sans text-sm sm:text-base leading-relaxed text-[#c8c8c0] whitespace-pre-line max-w-2xl">
            {post.captionWritten}
          </p>
        )}

        {post.keywords?.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2">
            {post.keywords.map((k) => (
              <span key={k} className="font-mono text-[10px] uppercase tracking-widest text-[#555550]">
                #{k}
              </span>
            ))}
          </div>
        )}

        <a
          href={post.postUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={TRIAL.externalLink}
          className="mt-8 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-[#888880] hover:text-[#CCFF00] transition-colors min-h-[44px]"
        >
          View original post on Instagram <ExternalLink size={12} />
        </a>
      </div>
    </div>
  );
}
